import { makeStyles, tokens, shorthands } from '@fluentui/react-components';

export const useStatusPanelPageStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    ...shorthands.gap(tokens.spacingVerticalM),
    '@media (max-width: 768px)': {
      ...shorthands.gap(tokens.spacingVerticalS),
    },
  },
  wallScreenRoot: {
    minHeight: '100vh',
    height: '100dvh',
    width: '100vw',
    maxWidth: '100vw',
    boxSizing: 'border-box',
    overflowX: 'hidden',
    backgroundColor: '#010101',
    color: '#deeaea',
    ...shorthands.gap('0'),
    ...shorthands.padding('28px', '36px'),
    fontFamily: "'Barlow Condensed', sans-serif",
    '@media (max-width: 1024px)': {
      ...shorthands.padding('20px', '22px'),
    },
    '@media (max-width: 768px)': {
      ...shorthands.padding('12px', '14px'),
    },
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    rowGap: tokens.spacingVerticalS,
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalL),
    ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke1),
    '@media (max-width: 768px)': {
      ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    },
  },
  wallScreenToolbar: {
    ...shorthands.padding('0', '0', '18px', '0'),
    ...shorthands.borderBottom('1px', 'solid', 'rgba(0,185,181,0.10)'),
    marginBottom: '22px',
    '@media (max-width: 768px)': {
      marginBottom: '14px',
      rowGap: '10px',
    },
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  wallScreenToolbarLeft: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    columnGap: '0',
    rowGap: '6px',
  },
  toolbarRight: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
    rowGap: tokens.spacingVerticalXS,
    '@media (max-width: 768px)': {
      columnGap: tokens.spacingHorizontalS,
      width: '100%',
    },
  },
  wallScreenToolbarRight: {
    flexDirection: 'row',
    alignItems: 'baseline',
    columnGap: '16px',
    flexShrink: 0,
    '@media (max-width: 768px)': {
      width: '100%',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      rowGap: '8px',
    },
  },
  content: {
    flexGrow: 1,
    minHeight: 0,
    overflowY: 'auto',
    ...shorthands.padding('0', tokens.spacingHorizontalL, tokens.spacingVerticalL),
    '@media (max-width: 768px)': {
      ...shorthands.padding('0', tokens.spacingHorizontalM, tokens.spacingVerticalM),
    },
  },
  wallScreenContent: {
    ...shorthands.padding('0'),
    overflow: 'hidden',
    flexGrow: 1,
  },
  wallScreenTitle: {
    fontSize: '44px',
    fontWeight: 900,
    letterSpacing: '2px',
    textTransform: 'uppercase',
    lineHeight: 1,
    color: '#deeaea',
    fontFamily: "'Barlow Condensed', sans-serif",
    '@media (max-width: 1024px)': {
      fontSize: '36px',
    },
    '@media (max-width: 768px)': {
      fontSize: '28px',
      letterSpacing: '1px',
    },
  },
  wallScreenSubtitle: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px',
    color: '#00b9b5',
    letterSpacing: '4px',
    '@media (max-width: 768px)': {
      letterSpacing: '2px',
      fontSize: '10px',
    },
  },
  wallScreenTime: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '34px',
    color: '#00b9b5',
    letterSpacing: '2px',
    lineHeight: 1,
    whiteSpace: 'nowrap',
    flexShrink: 0,
    '@media (max-width: 1024px)': {
      fontSize: '28px',
    },
    '@media (max-width: 768px)': {
      fontSize: '24px',
      letterSpacing: '1px',
    },
  },
  wallScreenDate: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '13px',
    color: '#deeaea',
    letterSpacing: '2px',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    '@media (max-width: 768px)': {
      fontSize: '11px',
      letterSpacing: '1px',
    },
  },
  escHint: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  statsBar: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
    gap: '10px',
    marginBottom: '22px',
    '@media (max-width: 1024px)': {
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    },
    '@media (max-width: 768px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      marginBottom: '14px',
      gap: '8px',
    },
  },
  statCard: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '14px',
    ...shorthands.padding('14px', '18px'),
    backgroundColor: '#060c0c',
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    minWidth: 0,
    '@media (max-width: 768px)': {
      ...shorthands.padding('10px', '12px'),
      gap: '10px',
    },
  },
  statValue: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '42px',
    lineHeight: 1,
    flexShrink: 0,
    '@media (max-width: 1024px)': {
      fontSize: '34px',
    },
    '@media (max-width: 768px)': {
      fontSize: '26px',
    },
  },
  statLabel: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontSize: '14px',
    color: '#deeaea',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    '@media (max-width: 768px)': {
      fontSize: '12px',
      letterSpacing: '1px',
    },
  },
  wallScreenFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ...shorthands.borderTop('1px', 'solid', '#142425'),
    ...shorthands.padding('12px', '0', '0', '0'),
    marginTop: '16px',
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      rowGap: '8px',
      marginTop: '10px',
    },
  },
  wallScreenFooterText: {
    fontFamily: "'Share Tech Mono', monospace",
    fontSize: '11px',
    color: '#deeaea',
    letterSpacing: '1.5px',
    '@media (max-width: 768px)': {
      fontSize: '10px',
      letterSpacing: '1px',
    },
  },
  wallScreenExitButton: {
    color: '#deeaea',
  },
});
